import { text } from "d3";
import _ from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Dropdown, DropdownItemProps, DropdownProps } from "semantic-ui-react";
import { restRequest } from "../../http";
import { LanguageContext } from "../../context";


const EmptyValue = '-^-empty-^-'
export default function LewittAddableDropdown(props: DropdownProps & { emptyText?: string }) {
    const newProps: DropdownProps = {
        ...props, allowAdditions: true, search: true, selection: true, fluid: true
    };

    if (newProps.multiple) {
        if (!newProps.value) {
            newProps.value = [];
        }
    
        if (!newProps.options) {
            newProps.options = []
        }
        
        if (newProps.value && (newProps.value as string[]).length > 0) {
            for (const v of (newProps.value as string[])) {
                if (!newProps.options.find(o => o.value === v)) {
                    newProps.options.push({ text: v, value: v, key: v });
                }
            }
        }
    } else {
        if (!newProps.options) {
            newProps.options = []
        }
    
        newProps.options.unshift({ value: EmptyValue, text: props.emptyText || '-None-', key: '-None-' })
    
        if (newProps.value) {
            if (!newProps.options.find(o => o.value === props.value)) {
                newProps.options.push({ text: props.value, value: props.value as any, key: props.value });
            }
        } else {
            newProps.value = EmptyValue;
        }

        if (newProps.onChange) {
            const oldOnChange = newProps.onChange;
            newProps.onChange = (e, d) => {
                if (d.value === EmptyValue) {
                    oldOnChange(e, {...d, value: undefined});
                } else {
                    oldOnChange(e, d);
                }
            }
        }

        if (newProps.onBlur) {
            const oldOnBlur = newProps.onBlur;
            newProps.onBlur = (e, d) => {
                if (d.value === EmptyValue) {
                    oldOnBlur(e, {...d, value: undefined});
                } else {
                    oldOnBlur(e, d);
                }
            }
        }
    }
    

    return (
        <Dropdown {...newProps} />
    );
}


export function LewittRemoteSearchDropdown(props: DropdownProps & {url: string, emptyText?: string }) {
    const sOptions = useState<DropdownItemProps[]>([]);
    const sKey = useState('');
    const sLoading = useState(false);
    const [t] = useContext(LanguageContext);

    const debundSetKey = useRef(_.debounce((key: string) => {
        sKey[1](key);
    }, 300))

    useEffect(() => {
        sLoading[1](true);
        restRequest<DropdownItemProps[]>(props.url, {key: sKey[0]}, 'post', undefined, 'user_dropdown_' + sKey[0]).then(values => {
            const emptyList = props.multiple ? [] : [{ value: EmptyValue, text: props.emptyText || '-None-', key: '-None-' } as any]
            sOptions[1](emptyList.concat(values.map(v => ({...v, key: v.value}))));
        }).finally(() => sLoading[1](false));
    }, [props.url, props.multiple, sKey[0]]);


    useEffect(() => {
        if (!props.multiple) {
            const emptyO = sOptions[0].find(o => o.value === EmptyValue);

            const text = props.emptyText || `-${t('None')}-`;
            if (!emptyO) {
                sOptions[1]([{ value: EmptyValue, text: text, key: '-None-' }, ...sOptions[0]]);
            } else if (emptyO.text !== text) {
                emptyO.text = text;
                sOptions[1]([...sOptions[0]]);
            }
        }
    }, [sOptions[0], props.emptyText, props.multiple, t]);

    const newProps: DropdownProps = {
        ...props, options: sOptions[0], loading: sLoading[0],
        onSearchChange: (e, d) => {
            debundSetKey.current(d.searchQuery);
        },
        search(options, value: string) {
            return options.filter(o => {
                const lValue = value.toLocaleLowerCase();
                return options.filter(o => (o.text && (o.text + '').toLowerCase().includes(lValue)) || (o.description && (o.description + '').toLowerCase().includes(lValue)));
            });
        }
    };

    if (newProps.multiple) {
        if (!newProps.value) {
            newProps.value = [];
        }
    
        if (!newProps.options) {
            newProps.options = []
        }
        
        if (newProps.value && (newProps.value as string[]).length > 0) {
            for (const v of (newProps.value as string[])) {
                if (!newProps.options.find(o => o.value === v)) {
                    newProps.options.push({ text: v, value: v, key: v });
                }
            }
        }
    } else {
        if (!newProps.options) {
            newProps.options = []
        }

        if (newProps.value) {
            if (!newProps.options.find(o => o.value === props.value)) {
                newProps.options.push({ text: props.value, value: props.value as any, key: props.value });
            }
        } else {
            newProps.value = EmptyValue;
        }
    

        if (newProps.onChange) {
            const oldOnChange = newProps.onChange;
            newProps.onChange = (e, d) => {
                if (d.value === EmptyValue) {
                    oldOnChange(e, {...d, value: undefined});
                } else {
                    oldOnChange(e, d);
                }
            }
        }

        if (newProps.onBlur) {
            const oldOnBlur = newProps.onBlur;
            newProps.onBlur = (e, d) => {
                if (d.value === EmptyValue) {
                    oldOnBlur(e, {...d, value: undefined});
                } else {
                    oldOnBlur(e, d);
                }
            }
        }
    }
    
    return (
        <Dropdown {...newProps}/>
    );
}